import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeCustomizationProvider } from './context/ThemeContext';
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import LoginPage from './modules/auth/pages/LoginPage';
import ForbiddenPage from './modules/auth/pages/ForbiddenPage';
import SetPassword from './modules/auth/pages/SetPassword';
import ForgotPasswordPage from './modules/auth/pages/ForgotPasswordPage';
import DashboardPage from './modules/dashboard/pages/DashboardPage';
import { UserList, UserProfile, UserProfileView } from './modules/users';
import { CompanyList } from './modules/companies';
import CompanyDetail from './modules/companies/pages/CompanyDetail';
import { AreaList } from './modules/areas';
import { RetentionList, RetentionDetail } from './modules/retentions';
import RetentionLineDetail from './modules/retentions/pages/RetentionLineDetail';
import { CorrespondenceTypeList } from './modules/correspondence-types';
import { TemplateList } from './modules/templates';
import { ProceedingList, ProceedingDetail, BulkImportHistoryPage } from './modules/proceedings';
import { CorrespondenceList, CorrespondenceDetail } from './modules/correspondences';
import { EntityList } from './modules/entities';
import { SurveyPage, SurveyResultsPage } from './modules/surveys';
import { CustodiaPage } from './modules/custodia';
import { WarehouseList, WarehouseDetail, BoxDetail } from './modules/warehouses';
import { RoleList } from './modules/roles';
import { PermissionList } from './modules/permissions';
import NotificationsPage from './modules/notifications/pages/NotificationsPage';
import DocumentDashboard from './modules/documents/pages/DocumentDashboard';
import DocumentDetail from './modules/documents/pages/DocumentDetail';
import ExternalPortal from './modules/entities/pages/ExternalPortal';
import ProtectedRoute from './components/ProtectedRoute';
import LandingPage from './pages/public/LandingPage';
import PQRSPage from './pages/public/PQRSPage';
import CorrespondencePortalPage from './pages/public/CorrespondencePortalPage';
import OneDriveCallback from './pages/OneDriveCallback';
import UserSupportPage from './modules/support-tickets/pages/UserSupportPage';
import AdminTicketsPage from './modules/support-tickets/pages/AdminTicketsPage';
import TicketDetailPage from './modules/support-tickets/pages/TicketDetailPage';

import { useAuth } from './hooks/useAuth';

// Guards auxiliares para Companies
const CompanyListGuard = () => {
  const { user, isOwner } = useAuth();

  if (isOwner) {
    return <CompanyList />;
  }

  if (user?.companyId) {
    return <Navigate to={`/companies/${user.companyId}`} replace />;
  }

  return <Navigate to="/403" replace />;
};

const CompanyDetailGuard = () => {
  const { user, isOwner } = useAuth();
  const params = useParams(); // Necesitamos useParams aquí
  const targetId = parseInt(params.id);

  if (isOwner) {
    return <CompanyDetail />;
  }

  // Verificar si es su propia empresa
  if (user?.companyId && parseInt(user.companyId) === targetId) {
    return <CompanyDetail />;
  }

  return <ForbiddenPage />;
};

function App() {
  return (
    <ThemeCustomizationProvider>
      <AuthProvider>
        <Routes>
        {/* Ruta pública - Landing Page */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Ruta pública - PQRS */}
        <Route path="/pqrs" element={<PQRSPage />} />

        {/* Ruta pública - Portal de Correspondencia */}
        <Route path="/correspondence/:companyId/company" element={<CorrespondencePortalPage />} />

        {/* Ruta pública - OneDrive OAuth Callback */}
        <Route path="/onedrive-callback" element={<OneDriveCallback />} />

        {/* Rutas de autenticación */}
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/403" element={<ForbiddenPage />} />
        </Route>

        {/* Establecer/recuperar contraseña (pública, sin layout) */}
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Rutas protegidas */}
        <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/companies"
            element={
              <ProtectedRoute requiredPermission="company.view">
                <CompanyListGuard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies/:id"
            element={
              <ProtectedRoute requiredPermission="company.view">
                <CompanyDetailGuard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/areas"
            element={
              <ProtectedRoute requiredPermission="area.view">
                <AreaList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/retentions"
            element={
              <ProtectedRoute requiredPermission="retention.view" requiredModule="retentions">
                <RetentionList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/retentions/:id"
            element={
              <ProtectedRoute requiredPermission="retention.view" requiredModule="retentions">
                <RetentionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/retentions/lines/:lineId"
            element={
              <ProtectedRoute requiredPermission="retention.view" requiredModule="retentions">
                <RetentionLineDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/correspondence-types"
            element={
              <ProtectedRoute requiredPermission="correspondence_type.view">
                <CorrespondenceTypeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute requiredPermission="template.view" requiredModule="templates">
                <TemplateList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/proceedings"
            element={
              <ProtectedRoute requiredPermission="proceeding.view" requiredModule="proceedings">
                <ProceedingList />
              </ProtectedRoute>
            }
          />
          <Route path="/proceedings/import-history" element={<BulkImportHistoryPage />} />
          <Route
            path="/proceedings/:id"
            element={
              <ProtectedRoute requiredPermission="proceeding.view" requiredModule="proceedings">
                <ProceedingDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/correspondences"
            element={
              <ProtectedRoute requiredPermission="correspondence.view" requiredModule="correspondences">
                <CorrespondenceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/correspondences/:id"
            element={
              <ProtectedRoute requiredPermission="correspondence.view" requiredModule="correspondences">
                <CorrespondenceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entities"
            element={
              <ProtectedRoute requiredPermission="entity.view" requiredModule="entities">
                <EntityList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses"
            element={
              <ProtectedRoute requiredPermission="warehouse.view" requiredModule="warehouses">
                <WarehouseList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses/:id"
            element={
              <ProtectedRoute requiredPermission="warehouse.view" requiredModule="warehouses">
                <WarehouseDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouses/boxes/:id"
            element={
              <ProtectedRoute requiredPermission="warehouse.view" requiredModule="warehouses">
                <BoxDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredPermission="user.view">
                <UserList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute requiredPermission="user.view">
                <UserProfileView />
              </ProtectedRoute>
            }
          />
          <Route path="/profile" element={<UserProfile />} />
          <Route
            path="/roles"
            element={
              <ProtectedRoute requiredPermission="role.view">
                <RoleList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute requiredPermission="permission.view">
                <PermissionList />
              </ProtectedRoute>
            }
          />
          <Route path="/notifications" element={<NotificationsPage />} />
          
          {/* Surveys / Instrumentos Routes */}
          <Route path="/surveys" element={<SurveyPage />} />
          <Route path="/surveys/results" element={<SurveyResultsPage />} />

          {/* Custodia Digital */}
          <Route path="/custodia" element={<CustodiaPage />} />

          {/* Support Tickets Routes */}
          <Route path="/support" element={<UserSupportPage />} />
          <Route path="/support/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/support/admin" element={<AdminTicketsPage />} />
          <Route path="/support/admin/tickets/:id" element={<TicketDetailPage />} />

          {/* Documents Dashboard */}
          <Route
            path="/documents/dashboard"
            element={
              <ProtectedRoute requiredPermission="document.dashboard" requiredModule="documents">
                <DocumentDashboard />
              </ProtectedRoute>
            }
          />
          {/* Document Detail */}
          <Route
            path="/documents/:id"
            element={
              <ProtectedRoute requiredPermission="document.view" requiredModule="documents">
                <DocumentDetail />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* External Portal (public) */}
        <Route path="/external/portal" element={<ExternalPortal />} />

        {/* Ruta por defecto */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AuthProvider>
    </ThemeCustomizationProvider>
  );
}

export default App;
